import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { useSubscription } from '../../hooks/useSubscription';
import { useToast } from '../../contexts/ToastContext';
import { CheckCircle, Crown, Zap, Users, BarChart3, HeadphonesIcon } from 'lucide-react';

const SubscriptionUpgradeModal = ({ isOpen, onClose }) => {
  const { subscriptionTiers, upgradeSubscription, loading } = useSubscription();
  const [selectedTier, setSelectedTier] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(false);
  const addToast = useToast();

  const handleUpgrade = async () => {
    if (!selectedTier) {
      addToast('Please select a subscription tier', 'error');
      return;
    }

    try {
      setUpgrading(true);
      const result = await upgradeSubscription(selectedTier.name, billingCycle);
      
      if (result.success) {
        addToast('Subscription upgrade initiated!', 'success');
        // In real implementation, redirect to Stripe checkout
        console.log('Redirecting to:', result.checkoutUrl);
        onClose();
      }
    } catch (error) {
      addToast(`Error upgrading subscription: ${error.message}`, 'error');
    } finally {
      setUpgrading(false);
    }
  };

  const getFeatureIcon = (featureName) => {
    switch (featureName) {
      case 'priority_support':
        return <HeadphonesIcon className="h-4 w-4 text-blue-500" />;
      case 'advanced_analytics':
        return <BarChart3 className="h-4 w-4 text-purple-500" />;
      case 'team_collaboration':
        return <Users className="h-4 w-4 text-green-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatFeatureName = (featureName) => {
    return featureName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Choose Your Plan
          </DialogTitle>
          <p className="text-center text-gray-600 mt-2">
            Upgrade your account to unlock powerful features
          </p>
        </DialogHeader>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <Button
              variant={billingCycle === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </Button>
            <Button
              variant={billingCycle === 'annual' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <Badge variant="secondary" className="ml-2">
                Save 17%
              </Badge>
            </Button>
          </div>
        </div>

        {/* Subscription Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {subscriptionTiers.map((tier) => (
            <Card
              key={tier.id}
              className={`cursor-pointer transition-all ${
                selectedTier?.id === tier.id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'hover:border-gray-300'
              } ${tier.name === 'pro' ? 'border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50' : ''}`}
              onClick={() => setSelectedTier(tier)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {tier.name === 'pro' && <Crown className="h-5 w-5 text-yellow-500" />}
                    <h3 className="text-xl font-semibold">{tier.display_name}</h3>
                    {tier.name === 'pro' && (
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        Most Popular
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-gray-600 text-sm">{tier.description}</p>
                <div className="mt-4">
                  <div className="text-3xl font-bold">
                    ${billingCycle === 'monthly' ? tier.price_monthly : tier.price_annual}
                    <span className="text-sm font-normal text-gray-500">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === 'annual' && tier.price_monthly > 0 && (
                    <div className="text-sm text-gray-500">
                      ${(tier.price_monthly * 12).toFixed(2)} billed annually
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Usage Limits */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-gray-700">Usage Limits</h4>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">
                        {tier.max_project_groups === -1 ? 'Unlimited' : tier.max_project_groups} project groups
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">
                        {tier.max_projects_per_group === -1 ? 'Unlimited' : tier.max_projects_per_group} projects per group
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">
                        {tier.max_tasks_per_project === -1 ? 'Unlimited' : tier.max_tasks_per_project} tasks per project
                      </span>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-gray-700">Features</h4>
                  <div className="space-y-1">
                    {Object.entries(tier.features || {}).map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center space-x-2">
                        {enabled ? (
                          getFeatureIcon(feature)
                        ) : (
                          <div className="h-4 w-4 rounded-full border border-gray-300"></div>
                        )}
                        <span className={`text-sm ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                          {formatFeatureName(feature)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={!selectedTier || upgrading || selectedTier.name === 'free'}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {upgrading ? (
              <>
                <Zap className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Crown className="h-4 w-4 mr-2" />
                {selectedTier?.name === 'free' ? 'Current Plan' : 'Upgrade Now'}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionUpgradeModal;
